import {
  resetPasswordCognitoUser,
  confirmResetPasswordCognitoUser,
  sendPasswordResetConfirmationEmail,
} from "api/user";
import { useState } from "react";
import { toast } from "react-toastify";
import errorCopy from "utils/error-copy";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import NewPassword from "sections/forgot-password/new-password";
import ResetPassword from "sections/forgot-password/reset-password";
import ValuePropPanel from "components/auth/value-prop-panel";
import FeatureTicker from "components/auth/feature-ticker";

const MOBILE_AVATARS = ["SM", "JT", "RL"];

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [showNewPassword, setNewPassword] = useState(false);

  const onSubmitResetPassword = async (data) => {
    try {
      setLoading(true);
      await resetPasswordCognitoUser(data.email);
      setUserEmail(data.email);
      setNewPassword(true);
    } catch (error) {
      toast.error(errorCopy(error.message || "Failed to send reset code. Please try again."), {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmitNewPassword = async (data) => {
    try {
      const code = [
        data.code0, data.code1, data.code2,
        data.code3, data.code4, data.code5,
      ].join("");
      setLoading(true);
      await confirmResetPasswordCognitoUser(userEmail, code, data.password);
      try {
        await sendPasswordResetConfirmationEmail(userEmail);
      } catch {
        // confirmation email failure is non-critical
      }
      toast.success("Password updated successfully.", { position: "top-right" });
      navigate("/login");
    } catch (error) {
      toast.error(errorCopy(error.message || "Failed to set new password. Please try again."), {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row font-dm-sans">
      <Helmet>
        <title>Reset Password | NirvaScribe</title>
      </Helmet>

      {/* Desktop: left value-prop panel */}
      <div className="hidden md:block md:w-1/2 shrink-0">
        <ValuePropPanel />
      </div>

      {/* Mobile: compact header */}
      <div className="md:hidden bg-deep relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute top-0 right-0 w-40 h-40 rounded-full bg-accent-light/15 blur-3xl pointer-events-none"
        />
        <div className="relative z-10 px-5 pt-4 pb-0">
          <div className="flex items-center justify-between mb-3">
            <img src="/brand-mark.png" alt="NirvaScribe" className="h-7 w-7" />
            <div className="flex items-center gap-1.5">
              <div className="flex -space-x-1.5">
                {MOBILE_AVATARS.map((init, i) => (
                  <div
                    key={i}
                    className="w-[22px] h-[22px] rounded-full bg-accent-light border-2 border-deep flex items-center justify-center text-[8px] text-deep font-medium"
                  >
                    {init}
                  </div>
                ))}
              </div>
              <span className="text-[10px] text-white/70">Aussie clinicians</span>
            </div>
          </div>
          <h1 className="font-display text-[22px] font-normal text-white leading-tight tracking-[-0.015em] mb-3">
              Notes done before the door closes.
            </h1>
          <FeatureTicker dark />
        </div>
      </div>

      {/* Right panel: form card */}
      <div className="flex-1 md:w-1/2 bg-white md:bg-shade-50 flex flex-col md:items-center md:justify-center md:p-6 overflow-y-auto">
        <div className="w-full md:max-w-[440px] md:bg-white md:rounded-ns p-6 md:p-7">
          {/* Logo on card — desktop only */}
          <div className="hidden md:block mb-5">
            <img src="/brand-lockup.png" alt="NirvaScribe" className="h-8" />
          </div>

          {showNewPassword ? (
            <NewPassword loading={loading} onSubmit={onSubmitNewPassword} />
          ) : (
            <ResetPassword loading={loading} onSubmit={onSubmitResetPassword} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
